import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import UsdtPaymentPanel from './UsdtPaymentPanel';
import ZellePaymentPanel from './ZellePaymentPanel';
import BcvReferencePrice from './BcvReferencePrice';

/**
 * CUALpay - Sistema de pagos oficial de CUALAPP.
 *
 * Hub unificado que orquesta los 3 metodos de pago disponibles:
 *  - Tarjeta de credito/debito via Stripe
 *  - USDT en red TRON (TRC-20)
 *  - Zelle (transferencia entre bancos de EEUU)
 *
 * Y muestra el roadmap de proximos metodos:
 *  - Pago Movil C2P (Venezuela, en lista de espera)
 *  - Otros segun roadmap
 *
 * Props:
 *  - levels: lista de niveles de Celula disponibles para comprar
 *  - onSuccess: callback al confirmar un pago
 *  - onStripeCheckout: callback al iniciar checkout Stripe (nivel)
 *  - isVenezuela: si se muestra la referencia BCV bajo precios
 *  - purchasing: estado de loading Stripe
 */
const CualpayHub = ({ levels, onSuccess, onStripeCheckout, isVenezuela, purchasing }) => {
  const [method, setMethod] = useState('card');
  const [stripeConfig, setStripeConfig] = useState({ enabled: true, mode: 'test' });

  useEffect(() => {
    // Aqui podriamos consultar /api/cualpay/status para saber dinamicamente
    // que metodos estan activos. Por ahora hardcoded.
    Promise.all([
      axios.get(`${API}/usdt/config`).catch(() => ({ data: { enabled: false } })),
      axios.get(`${API}/zelle/config`).catch(() => ({ data: { enabled: false } })),
    ]).then(([usdt, zelle]) => {
      // El config se usa dentro de cada panel individualmente, esto es solo placeholder
      // para futura expansion.
    });
  }, []);

  const methods = [
    { id: 'card', label: 'Tarjeta', icon: '\u{1F4B3}', subtitle: 'Stripe' },
    { id: 'usdt', label: 'USDT', icon: '\u20AE', subtitle: 'Red TRON' },
    { id: 'zelle', label: 'Zelle', icon: '\u{1F4B5}', subtitle: 'Bancos USA' },
  ];

  return (
    <div className="space-y-4" data-testid="cualpay-hub">
      {/* Branding header */}
      <div className="flex items-center justify-between flex-wrap gap-2 border-b border-gray-100 pb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-widest bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            CUALpay
          </span>
          <span className="text-[10px] text-gray-400">| Sistema de pagos del ecosistema CUALAPP</span>
        </div>
        {stripeConfig.mode === 'test' && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="cualpay-test-mode">
            MODO PRUEBAS
          </span>
        )}
      </div>

      {/* Tabs de metodos */}
      <div className="flex flex-wrap gap-1 sm:gap-2 border-b border-gray-200" data-testid="cualpay-tabs">
        {methods.map(m => (
          <button
            key={m.id}
            onClick={() => setMethod(m.id)}
            data-testid={`cualpay-tab-${m.id}`}
            className={`px-3 sm:px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition flex items-center gap-1.5 ${method === m.id
              ? 'border-purple-600 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <span>{m.icon}</span>
            <span>{m.label}</span>
            <span className="hidden sm:inline text-[10px] text-gray-400">· {m.subtitle}</span>
          </button>
        ))}
      </div>

      {/* Panel del metodo activo */}
      {method === 'card' && (
        <div data-testid="cualpay-card-panel" className="space-y-3">
          <div className="text-xs text-gray-600 bg-purple-50 border border-purple-200 rounded p-3">
            Paga con tarjeta de credito o debito mediante <strong>Stripe</strong>. Procesador certificado PCI DSS Nivel 1.
            Soporta tarjetas Visa, Mastercard, American Express, Discover.
          </div>
          {levels && levels.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {levels.map(l => (
                <button
                  key={l.id}
                  onClick={() => onStripeCheckout && onStripeCheckout(l.id)}
                  disabled={purchasing}
                  data-testid={`cualpay-card-buy-${l.id}`}
                  className="p-3 rounded-lg border border-purple-200 bg-white hover:border-purple-400 text-left disabled:opacity-50 transition"
                >
                  <div className="text-xs font-semibold text-purple-800">{l.name}</div>
                  <div className="text-lg font-bold text-purple-900">${l.price_usd}</div>
                  {isVenezuela && <BcvReferencePrice usdAmount={l.price_usd} compact />}
                  <div className="text-[10px] text-purple-700 mt-1">{l.cualcoin_included} Cc</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {method === 'usdt' && (
        <UsdtPaymentPanel levels={levels} onSuccess={onSuccess} />
      )}

      {method === 'zelle' && (
        <ZellePaymentPanel levels={levels} onSuccess={onSuccess} />
      )}

      {/* Roadmap visible: proximos metodos */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-2">Proximamente</p>
        <div className="flex flex-wrap gap-2 text-[11px] text-gray-500">
          <span data-testid="cualpay-soon-pagomovil" className="px-2 py-1 rounded-full bg-gray-100">{'\u{1F4F1}'} Pago Movil VE</span>
          <span className="px-2 py-1 rounded-full bg-gray-100">{'\u{1F1E7}\u{1F1F7}'} PIX Brasil</span>
          <span className="px-2 py-1 rounded-full bg-gray-100">{'\u{1F1F2}\u{1F1FD}'} SPEI Mexico</span>
          <span className="px-2 py-1 rounded-full bg-gray-100">{'\u{1F4B0}'} Cripto adicional (BTC, ETH)</span>
        </div>
      </div>
    </div>
  );
};

export default CualpayHub;
